import React, { useEffect, useState } from 'react';
import { isAwaitingProcessing } from '../../utils/articleReadiness';
import './ArticlesList.css';
import ArticlesView from './ArticlesView';
import {
    addArticle,
    setSelectedArticlesArray,
    setSelectedClaimsArray,
    setUrlToBiasMapping,
    setUrlToRankMapping,
} from './slice';
import { batch, useDispatch, useSelector } from 'react-redux';
import { selectSelectedArticlesArray, selectSelectedClaimsArray } from '../../app/store';
import { api } from '../../app/http-common';
import LottieLoader from '../../components/LottieLoader';
import {normalThemes, formatDate, extractRootDomain} from './transformers';
import { debounce } from 'lodash';

const ArticlesList = ({
                          articles,
                          claims,
                          publicationsData,
                          onSelectedArticleChange,
                          onSelectedClaimChange,
                          publishers,
                          autoSelection,
                          theme,
                          setAutoSelect,
                          currentStep,
                          prs,
                          showToastNotification,
                      }) => {
    const selectedArticles = useSelector(selectSelectedArticlesArray);
    const selectedClaims = useSelector(selectSelectedClaimsArray);
    const dispatch = useDispatch();
    const urlToBiasMapping = useSelector((state) => state.articles.urlToBiasMapping);
    const urlToRankMapping = useSelector((state) => state.articles.urlToRankMapping);
    const [loadingArticle, setLoadingArticle] = useState(false);
    const [columns, setColumns] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMoreArticles, setHasMoreArticles] = useState(true);

    useEffect(() => {
        if (currentStep===2) {
            return;
        }

        if (publicationsData) {
            const newUrlToBiasMapping = publicationsData.reduce((acc, current) => {
                acc[current.url] = {
                    bias: current.bias,
                    sourceName: current.name,
                };
                return acc;
            }, {});
            dispatch(setUrlToBiasMapping(newUrlToBiasMapping));
        }
    }, [dispatch, publicationsData, currentStep]);

    useEffect(() => {
        // if (currentStep===2) {
        //     return;
        // }
        const pubs = publicationsData || publishers;
        if (pubs) {
            const newUrlToRankMapping = pubs.reduce((acc, current) => {
                acc[current.url] = {
                    rank: current.rank,
                    sourceName: current.name,
                };
                return acc;
            }, {});
            dispatch(setUrlToRankMapping(newUrlToRankMapping));
        }
    }, [dispatch, publishers, publicationsData, currentStep]);

    const setArticlesSourcesTitle = (articles,currentStep) => {
        return articles.map(article => {
            try {
                // Create a new object for the updated article
                const updatedArticle = { ...article };
                // Check if 'sources' is an array
                if (Array.isArray(article.sources) &&  article.sources.length) {
                    // Map over each source in the sources array to update titles
                    updatedArticle.sources = article.sources.map((_source,index) => {
                        let source = currentStep === 1 ? _source : _source.source;
                        try {
                            // Get mapping for the source URI

                            let uri;
                            if (currentStep === 1) {
                                if (index === 0) {
                                    uri = extractRootDomain(article.url);
                                   // uri = !source?.uri ? extractRootDomain(article.url) : source.uri;
                                    if (source?.uri && !urlToRankMapping[uri]) {
                                        uri = extractRootDomain(source.uri);
                                        if (!urlToRankMapping[uri]) {
                                            uri = source.uri;
                                        }
                                    }
                                } else {
                                    uri = !source?.uri ? null : source.uri;
                                }
                            } else { //claims
                                const claimArticle = selectedArticles.find(art => art._id === _source.articleId);
                                uri = extractRootDomain(claimArticle.url);
                                if (!urlToRankMapping[uri]) {
                                    uri = source.uri;
                                    if (!urlToRankMapping[uri]) {
                                        uri = extractRootDomain(uri);
                                    }
                                }
                            }
                            const mapping = urlToRankMapping[uri];
                            // Update the title if a mapping is found
                            if (mapping && mapping.sourceName) {

                                return {
                                    ...source,
                                    title: mapping.sourceName
                                };
                            } else {
                               // console.warn(`No mapping found for URI: ${source.uri} ${updatedArticle}`);
                                // Optionally, set a default title if mapping is missing

                                return {
                                    ...source,
                                    title: source.title
                                };
                            }
                        } catch (sourceError) {
                           // console.error(`Error processing source for URI: ${source.uri}`, sourceError);
                            // Return the original source if an error occurs
                            return source;
                        }
                    });
                   // console.log('Updated article:', updatedArticle);
                } else if (article.source && typeof article.source === 'object') {

                    const uri = !article.source?.uri ? extractRootDomain(article.url) :article.source.uri;
                    const mapping = urlToRankMapping[uri];
                    if (mapping && mapping.sourceName) {
                        // Create a new source object with the updated title
                        updatedArticle.source = {
                            ...article.source,
                            title: mapping.sourceName
                        };
                    } else {
                        // console.warn(`No mapping found for URI: ${article.source.uri}`);
                        // Optionally, set a default title
                        // updatedArticle.source = {
                        //     ...article.source,
                        //     title: 'Unknown Source'
                        // };
                    }
                }
                else {
                    console.warn(`Missing or invalid sources array for article: ${article.title}`);
                    // Set a default source if 'sources' array is missing or invalid
                    //updatedArticle.sources = [{ title: 'Unknown Source', uri: '' }];
                }
                return updatedArticle;
            } catch (error) {
                // Return the original article if an error occurs
                return article;
            }
        });
    };

    useEffect(() => {
        // §4 — articles that matched the scope the story was opened under come
        // first; everything else keeps the existing score order WITHIN each
        // group. resolveOpenedStoryArticles already partitions the list, but
        // the score sort below runs afterwards and would undo it, which is why
        // an English "Outside this filter" article could render above the
        // Hebrew ones that actually matched.
        //
        // `lockedOutOfScope` is the same question asked of a restricted
        // bucket's saved scope rather than the feed's live filter, so it sorts
        // identically — otherwise a story opened from a matching-only bucket
        // led with the articles it is forbidden to use.
        // `outsideTimeRange` demotes for the same reason: it is not in the set
        // the feed counted, whatever the reason it is not.
        const outOfScope = (a) => Boolean(a?.extra) || Boolean(a?.outsideTimeRange) || Boolean(a?.lockedOutOfScope) || isAwaitingProcessing(a);
        const byScopeThenScore = (a, b) =>
            (outOfScope(a) - outOfScope(b)) || (getArticleScore(a) - getArticleScore(b));

        const updateColumns = () => {
            let entries = currentStep===2  ? claims : articles;
            const singleColumn = currentStep===2 || (!publicationsData || normalThemes.includes(theme));

            if (currentStep === 1 || currentStep === 2) {
                entries = setArticlesSourcesTitle(entries,currentStep);
            }
            
            const validArticles = entries.filter((entry) => entry);
            const sortedArticles = (currentStep===2)? validArticles :singleColumn
                ? validArticles.sort(byScopeThenScore)
                : [];

            const firstColumnArticles = !singleColumn
                ? validArticles
                    .filter((article) => getBiasForArticleUrl(article) > 0 && getBiasForArticleUrl(article) < 8)
                    .sort(byScopeThenScore)
                : [];

            const secondColumnArticles = !singleColumn
                ? validArticles
                    .filter((article) => getBiasForArticleUrl(article) > 7 && getBiasForArticleUrl(article) < 15)
                    .sort(byScopeThenScore)
                : [];

            const thirdColumnArticles = !singleColumn
                ? validArticles
                    .filter((article) => getBiasForArticleUrl(article) > 14)
                    .sort(byScopeThenScore)
                : [];

            setColumns(!singleColumn ? [firstColumnArticles, secondColumnArticles, thirdColumnArticles] : [sortedArticles]);

            if (autoSelection) {
                if (currentStep===2) {
                    clearSelectedClaims();
                    dispatch(updateMultipleClaims(sortedArticles));
                } else {
                    clearSelectedArticles();
                    // Under a restriction, default the selection to exactly the
                    // articles the draft may actually be written from. Selecting
                    // sources the server will discard is worse than selecting
                    // none: it reads as a promise the job does not keep.
                    const selectable = (list) => list.filter(a => !a?.lockedOutOfScope);
                    dispatch(
                        updateMultipleArticles([
                            ...selectable(sortedArticles).slice(0, 21),
                            ...selectable(firstColumnArticles).slice(0, 7),
                            ...selectable(secondColumnArticles).slice(0, 7),
                            ...selectable(thirdColumnArticles).slice(0, 7),
                        ])
                    );
                }
                //setAutoSelect(false);
            }
        };

        const debouncedUpdateColumns = debounce(updateColumns, 300); // Debounce to optimize updates
        debouncedUpdateColumns();

        return () => debouncedUpdateColumns.cancel(); // Cleanup
    }, [dispatch, theme, articles, claims, urlToRankMapping, autoSelection,currentStep]);

    useEffect(() => {
        if (onSelectedArticleChange) {
            onSelectedArticleChange(selectedArticles.filter((article) => article.isSelected));
        }
    }, [selectedArticles, onSelectedArticleChange]);

    useEffect(() => {
        if (onSelectedClaimChange) {
            onSelectedClaimChange(selectedClaims.filter((claim) => claim.isSelected));
        }
    }, [selectedClaims, onSelectedClaimChange]);

    const handleArticleSelectionChange = (article, isSelected) => {
        if (prs && isSelected && selectedArticles.filter(a=>a.isSelected).length > 0) {
            return;
        }

        dispatch(
            setSelectedArticlesArray({
                article,
                isSelected,
            })
        );
    };

    const handleClaimSelectionChange = (claim, isSelected) => {
        dispatch(
            setSelectedClaimsArray({
                claim,
                isSelected,
            })
        );
    };

    const getBiasForArticleUrl = (article) => {
        if (!publicationsData) {
            return -1;
        }
        if (article.bias) {
            return article.bias;
        }
        let domainKey = Object.keys(urlToBiasMapping).find((domain) => article.url?.includes(domain));
        if (!domainKey && article.url?.includes('bbc.co.uk')) {
            domainKey = 'bbc.com';
        }
        return domainKey && urlToBiasMapping[domainKey] ? urlToBiasMapping[domainKey].bias : -1;
    };

    const getRankForArticleUrl = (article) => {
        if (!publishers) {
            return 101;
        }
        let domainKey = Object.keys(urlToRankMapping).find((domain) => article.url?.includes(domain));
        if (!domainKey && article.url?.includes('bbc.co.uk')) {
            domainKey = 'bbc.com';
        }
        return domainKey && urlToRankMapping[domainKey] ? urlToRankMapping[domainKey].rank : 100;
    };

    const getArticleScore = (article) => {
        const rank = getRankForArticleUrl(article);
        return calcArticleScore(rank, article.dateTimePub);
    };

    const calcArticleScore = (rank, publishDateISO) => {
        const maxRank = 100;
        const rankScore = (rank - 1) / (maxRank - 1);

        const currentDate = new Date();
        const publishDate = publishDateISO ? new Date(publishDateISO) : currentDate;
        const diffMinutes = Math.ceil(Math.abs(currentDate - publishDate) / (1000 * 60));

        const maxMinutes = 43200; // 30 days * 24 hours/day * 60 minutes/hour
        const dateScore = Math.min(1, diffMinutes / maxMinutes);

        const rankWeight = 0.5;
        const dateWeight = 0.5;
        return rankWeight * rankScore + dateWeight * dateScore;
    };

    const handleAddUrl = async (url) => {
        if (url) {
            const exist = articles.find((art) => art.url === url);
            if (exist) {
                alert('Already exists');
                return;
            }
            setLoadingArticle(true);
            let newArticle = await api.getArticle({
                article: {
                    url,
                },
            });
            setLoadingArticle(false);
            if (!newArticle || !newArticle.body) {
                alert(`${url}: Is not supported!`);
                return;
            }
            dispatch(setSelectedArticlesArray({ article: newArticle, isSelected: true }));
            dispatch(addArticle(newArticle));
        }
    };

    const clearSelectedArticles = () => {
        selectedArticles.forEach((article) => handleArticleSelectionChange(article, false));
    };

    const selectAllArticles = () => {
        const allArticles = columns.flat();
        allArticles.forEach((article) => {
            if (!selectedArticles.find(a => a.url === article.url && a.isSelected)) {
                dispatch(setSelectedArticlesArray({ article, isSelected: true }));
            }
        });
    };

    const handleFetchMore = async () => {
        if (isLoadingMore || !hasMoreArticles) return;
        setIsLoadingMore(true);
        try {
            // Get storyId from the first article's groupSubject
            const storyId = articles[0]?.groupSubject;
            if (!storyId) {
                setHasMoreArticles(false);
                return;
            }
            const moreArticles = await api.articlesByStory(storyId, articles.length, 200);
            if (!moreArticles || moreArticles.length === 0) {
                setHasMoreArticles(false);
            } else {
                // Dispatch action to add articles to Redux store
                moreArticles.forEach(article => dispatch(addArticle(article)));
            }
        } catch (error) {
            console.error('Error fetching more articles:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const clearSelectedClaims = () => {
        selectedClaims.forEach((claim) => handleClaimSelectionChange(claim, false));
    };

    const updateMultipleArticles = (articles) => {
        return (dispatch) => {
            batch(() => {
                articles.forEach((article) => {
                    dispatch(setSelectedArticlesArray({ article, isSelected: true }));
                });
            });
        };
    };

    const updateMultipleClaims = (claims) => {
        return (dispatch) => {
            batch(() => {
                claims.forEach((claim) => {
                    dispatch(setSelectedClaimsArray({ claim, isSelected: true }));
                });
            });
        };
    };

    return (
        <div>
            {loadingArticle && (
                <div className="spinner-overlay">
                    <LottieLoader size={600}/>
                </div>
            )}
            {/* Step 1 header is rendered by CreateBriefComponent's .sa-header */}
            {/* Step 2 header removed — Figma shows no sub-heading on Claims */}
            <hr style={{
                border: '7px solid gray',
                marginLeft: '25px',
                marginBottom: '1px',
                marginTop: '1px',
                marginRight: '1px'
            }}/>
            <div className="articles-container">

                {columns && (
                    <ArticlesView
                        columns={columns}
                        handleArticleSelectionChange={handleArticleSelectionChange}
                        handleClaimsSelectionChange={handleClaimSelectionChange}
                        selectedArticles={selectedArticles}
                        selectedClaims={selectedClaims}
                        formatDate={formatDate}
                        handleAddUrl={handleAddUrl}
                        dragable={currentStep === 2}
                        currentStep = {currentStep}
                        prs = {prs}
                        showToastNotification={showToastNotification}
                    />
                )}
            </div>
        </div>
    );
};

export default ArticlesList;
